import { RequestModelInterface } from "@/service/models/request-model-interface";
import { ResponseModelInterface } from "@/service/models/response-model-interface";
import { BaseDosageChoiceVM } from "@/service/view-models/dosage-choices/base-dosage-choice-vm";
import { XmlDecodingException } from "@/utilities/exceptions/xml-decoding-exception";
import { Duration } from "@/utilities/types/duration";
import { XmlDocumentWrapper } from "@/utilities/xml-helpers/xml-document-wrapper";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { DailyDosage, DosageRepeat, DosageSequence, LastingDosage, WeeklyDosage } from ".";
import { FormulationAndRoute } from "../parts/formulation-and-route";

export abstract class BaseDosageChoice implements RequestModelInterface, ResponseModelInterface
{
    public static readonly allowedTagNames = [
        "dosageRepeat", "dosageSequence", "lastingDosage", "dailyDosage", "weeklyDosage"
    ];

    /**
     * Construct and returns a ViewModel instance filled with the data inside the current model.
     */
    public abstract makeViewModel(): BaseDosageChoiceVM;

    /**
     * Givent any known dose date and an arbitrary date, tells what is the
     * closest date of the next dose to be taken. If current is already aligned,
     * an equivalent date must be returned.
     * 
     * @param knownDose         The date of any known dose
     * @param current           The date around which the closest dose should be searched
     */
    public abstract getClosestNextDoseDate(knownDose: Date, current: Date): Date;

    /**
     * Givent any known dose date and an arbitrary date, tells what is the
     * closest date of the previous dose taken. If current is already aligned,
     * an equivalent date must be returned.
     * 
     * @param knownDose         The date of any known dose
     * @param current           The date around which the closest dose should be searched
     */
    public abstract getClosestPreviousDoseDate(knownDose: Date, current: Date): Date;

    /**
     * Returns a dose value to be displayed by the GUI.
     */
    public abstract getDoseValueForDisplay(): number;

    /**
     * Returns a dose unit to be displayed by the GUI.
     */
    public abstract getDoseUnitForDisplay(): string;

    /**
     * Returns an infusion time to be displayed by the GUI.
     */
    public abstract getInfusionTimeForDisplay(): Duration|null;

    /**
     * Returns an interval between doses to be displayed by the GUI.
     */
    public abstract getIntervalForDisplay(): Duration;

    /**
     * Returns a FormulationAndRoute object to be displayed by the GUI.
     */
    public abstract getRouteForDisplay(): FormulationAndRoute;

    /**
     * @inheritdoc
     */
    public abstract getXml(doc: XmlDocumentWrapper): XmlElementWrapper;

    public static makeFromXmlGeneric(elem: XmlElementWrapper): BaseDosageChoice
    {
        switch (elem.element.tagName) {
            case "dosageRepeat":
                return DosageRepeat.makeFromXml(elem);

            case "dosageSequence":
                return DosageSequence.makeFromXml(elem);

            case "lastingDosage":
                return LastingDosage.makeFromXml(elem);

            case "dailyDosage":
                return DailyDosage.makeFromXml(elem);

            case "weeklyDosage":
                return WeeklyDosage.makeFromXml(elem);

            default:
                throw XmlDecodingException.noRequiredChildFound(elem.element, this.allowedTagNames);
        }
    }
}
